
import React, { useState, useEffect } from 'react';
import { useGraphTeams } from '@/hooks/useGraphTeams';
import { useToast } from '@/hooks/use-toast';
import LoggingService from '@/services/LoggingService';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import TeamCarousel from '@/components/teams/TeamCarousel';
import ComplianceChart from '@/components/teams/ComplianceChart';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import TeamHealthMetrics from '@/components/teams/TeamHealthMetrics';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import '@/styles/apple-style.css';

const Teams = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const { teams, loading, error, fetchTeams, debugGraphConnection, isConfigured } = useGraphTeams();
  const { toast } = useToast();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  
  useEffect(() => {
    LoggingService.debug('Teams page mounted', { isConfigured });
    
    if (isConfigured) {
      fetchTeams().catch(err => {
        LoggingService.error('Error fetching teams in Teams page', err);
        console.error('Error fetching teams:', err);
        
        toast({
          title: "Error fetching teams",
          description: `${err.message}. Running diagnostic check...`,
          variant: "destructive",
        });
      });
    }
  }, [isConfigured, fetchTeams, toast]);

  const filteredTeams = teams.filter(team => {
    const matchesSearch = team.displayName.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         (team.description?.toLowerCase().includes(searchTerm.toLowerCase()) || false);
    
    const statusMatch = 
      statusFilter === 'all' ? true :
      statusFilter === 'active' ? !team.isArchived :
      statusFilter === 'archived' ? team.isArchived :
      false;
    
    return matchesSearch && statusMatch;
  });
  
  return (
    <DashboardLayout>
      {/* Page Header */}
      <div className="mb-6">
        <p className="text-muted-foreground text-sm">Welcome Back to Teams Governance</p>
        <h1 className="text-2xl md:text-3xl font-bold text-foreground dark:text-foreground">{user?.email || 'Guest User'}</h1>
      </div>

      <div className={cn(
        "grid gap-6 md:gap-8",
        isMobile ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-4"
      )}>
        {/* Team Health Metrics - Takes one fourth on desktop */}
        <div className={cn(
          isMobile ? "order-2" : "lg:col-span-1"
        )}>
          <TeamHealthMetrics teams={teams} />
        </div>
        
        {/* Teams List - Takes three fourths on desktop */}
        <div className={cn(
          isMobile ? "order-1" : "lg:col-span-3"
        )}>
          <div className="mb-5">
            <h2 className="text-xl font-medium mb-1 text-foreground dark:text-foreground">Your teams</h2>
            <p className="text-muted-foreground text-sm">Monitor and manage team compliance with organizational governance policies.</p>
          </div>
          
          {/* Search and Filters - Stack on mobile */}
          <div className={cn(
            "flex flex-col space-y-4 mb-5",
            !isMobile && "sm:flex-row sm:space-y-0 justify-between mb-6"
          )}>
            <div className="relative w-full max-w-xs">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
              <Input
                placeholder="Search teams..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-full h-10 bg-card dark:bg-card"
              />
            </div>
            
            <div className={cn(
              "flex space-x-2",
              isMobile && "overflow-x-auto pb-2 w-full"
            )}>
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-4 py-2 rounded-full text-sm transition-colors ${statusFilter === 'all' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-card dark:bg-card text-muted-foreground border border-border dark:border-slate-700/50'}`}
              >
                All Teams
              </button>
              <button
                onClick={() => setStatusFilter('active')}
                className={`px-4 py-2 rounded-full text-sm transition-colors ${statusFilter === 'active' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-card dark:bg-card text-muted-foreground border border-border dark:border-slate-700/50'}`}
              >
                Active
              </button>
              <button
                onClick={() => setStatusFilter('archived')}
                className={`px-4 py-2 rounded-full text-sm transition-colors ${statusFilter === 'archived' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-card dark:bg-card text-muted-foreground border border-border dark:border-slate-700/50'}`}
              >
                Archived
              </button>
            </div>
          </div>
          
          {/* Teams Carousel - Adjusted for mobile */}
          <TeamCarousel 
            teams={filteredTeams}
            loading={loading}
            isConfigured={isConfigured}
          />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Teams;
